import type { InspectionType, PhotoSection, Role } from '@room-verify/shared';

export const API_URL: string =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, '') ?? '';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly context: Record<string, unknown> = {},
  ) {
    super(code);
    this.name = 'ApiError';
  }

  get isAuthExpiry(): boolean {
    return this.code === 'SESSION_EXPIRED' || this.code === 'UNAUTHORIZED';
  }
}

const TOKEN_KEY = 'roomverify.mgmt.token';

export const loadToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const saveToken = (token: string | null): void => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage may be blocked; the in-memory token still serves this session */
  }
};

async function parseError(response: Response): Promise<ApiError> {
  let code = 'INTERNAL_ERROR';
  let context: Record<string, unknown> = {};
  try {
    const payload = (await response.json()) as { error?: string } & Record<string, unknown>;
    if (typeof payload.error === 'string') code = payload.error;
    const { error: _ignored, ...rest } = payload;
    context = rest;
  } catch {
    /* a non-JSON body still deserves a typed failure */
  }
  return new ApiError(code, response.status, context);
}

async function request<T>(
  path: string,
  options: { method?: string; token?: string | null; json?: unknown } = {},
): Promise<T> {
  const headers = new Headers();
  if (options.token) headers.set('authorization', `Bearer ${options.token}`);
  let body: string | undefined;
  if (options.json !== undefined) {
    headers.set('content-type', 'application/json');
    body = JSON.stringify(options.json);
  }

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, { method: options.method ?? 'GET', headers, body });
  } catch (cause) {
    throw new ApiError('NETWORK', 0, { cause: String(cause) });
  }

  if (!response.ok) throw await parseError(response);
  return (await response.json()) as T;
}

/* ------------------------------------------------------------------ types */

export interface SessionUser {
  id: string;
  fullName: string;
  role: Role;
}

export interface DashboardStats {
  date: string;
  stats: {
    total: number;
    checkout: number;
    clean: number;
    avgDurationSeconds: number;
    withMissingItems: number;
    edited: number;
  };
  bySupervisor: { supervisorId: string; name: string; total: number }[];
}

export interface InspectionListItem {
  id: string;
  roomNumber: string;
  inspectionType: InspectionType;
  supervisorId: string;
  supervisorName: string;
  startedAt: string;
  completedAt: string;
  durationSeconds: number;
  businessDate: string;
  photoCount: number;
  itemsComplete: boolean;
  missingItems: string[];
  otherMissingItem: string | null;
  editCount: number;
}

export interface InspectionList {
  total: number;
  limit: number;
  offset: number;
  results: InspectionListItem[];
}

export interface ReportPhoto {
  id: string;
  section: PhotoSection;
  sequence: number;
  capturedAt: string;
  uploadedAt: string;
  hash: string;
  mimeType: string;
  byteSize: number;
  clockAdjusted: boolean;
  capturedBy: string;
}

export interface EditRecord {
  id: string;
  occurredAt: string;
  editorName: string | null;
  before: {
    roomNumber?: string;
    inspectionType?: InspectionType;
    itemsComplete?: boolean;
    missingItems?: string[];
    otherMissingItem?: string | null;
  } | null;
  after: EditRecord['before'];
}

export interface Report {
  inspection: {
    id: string;
    roomNumber: string;
    inspectionType: InspectionType;
    supervisorId: string;
    supervisorName: string;
    startedAt: string;
    completedAt: string;
    durationSeconds: number;
    businessDate: string;
    itemsComplete: boolean;
    missingItems: string[];
    otherMissingItem: string | null;
    editCount: number;
  };
  photos: ReportPhoto[];
  edits: EditRecord[];
}

export interface ListFilters {
  from?: string;
  to?: string;
  type?: InspectionType | '';
  room?: string;
  supervisorId?: string;
  missingItems?: 'yes' | 'no' | '';
  edited?: 'yes' | 'no' | '';
  /** Both are already supported by GET /management/inspections. */
  limit?: number;
  offset?: number;
}

const query = (filters: ListFilters): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `?${search}` : '';
};

export const api = {
  login: (username: string, password: string) =>
    request<{ token: string; expiresAt: string; user: SessionUser }>('/auth/login', {
      method: 'POST',
      json: { username, password },
    }),

  me: (token: string) => request<{ user: SessionUser }>('/auth/me', { token }),

  logout: (token: string) => request<{ ok: true }>('/auth/logout', { method: 'POST', token }),

  dashboard: (token: string, date: string) =>
    request<DashboardStats>(`/management/dashboard?date=${date}`, { token }),

  supervisors: (token: string) =>
    request<{ supervisors: { id: string; name: string; total: number }[] }>(
      '/management/supervisors',
      { token },
    ),

  inspections: (token: string, filters: ListFilters) =>
    request<InspectionList>(`/management/inspections${query(filters)}`, { token }),

  report: (token: string, id: string) => request<Report>(`/management/inspections/${id}`, { token }),

  editReport: (
    token: string,
    id: string,
    changes: {
      roomNumber: string;
      inspectionType: InspectionType;
      complete: boolean;
      missingItems: string[];
      otherMissingItem: string | null;
    },
  ) =>
    request<{ ok: true; unchanged: boolean }>(`/management/inspections/${id}`, {
      method: 'PATCH',
      token,
      json: changes,
    }),
};

/**
 * Fetches a photo as a blob URL.
 *
 * An <img src> cannot carry an Authorization header, and putting a credential in a
 * URL is not acceptable — so the bytes are fetched with the bearer token and handed
 * to the browser as an object URL. R2 stays private and no signed link is ever minted.
 */
export async function fetchPhotoObjectUrl(token: string, photoId: string): Promise<string> {
  const response = await fetch(`${API_URL}/management/photos/${photoId}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw await parseError(response);
  return URL.createObjectURL(await response.blob());
}
